import {
  Box,
  Button,
  FlexBox,
  Icon,
  Spinner,
  Switch,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import {
  ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePrevious } from 'react-use';
import { change } from 'redux-form';

import { useDixaMessengerApi } from 'components/settings/components/sections/dixaMessenger/api/apiProvider';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { messengerAppearanceFormName } from '../../../messengerSettingsFormValueSelector';
import { messengerSettingsFormFields } from '../../../types';
import { resolveLogoUrl } from '../../../utils';
import { FormSectionTitle } from '../../formSectionTitle';

import styles from './styles.module.scss';

interface MessengerLogoProps {
  logo: string | undefined;
}

export const MessengerLogo = ({ logo }: MessengerLogoProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [isEnabled, setIsEnabled] = useState<boolean>(logo !== undefined);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const notify = useNotifications();

  const messengerApi = useDixaMessengerApi();

  // When the logo form value is cleared, the switch should be toggled on/off
  // based on the initial value of the form field.
  const previousLogo = usePrevious(logo);
  useEffect(() => {
    if (!previousLogo && logo) {
      setIsEnabled(true);
    }
    if (previousLogo && !logo) {
      setIsEnabled(false);
    }
  }, [logo, previousLogo]);

  const fileInput = useRef<HTMLInputElement>(null);

  const openFilePicker = useCallback(() => {
    if (isUploading) return;
    fileInput.current?.click();
  }, [isUploading]);

  const clearSelectedFile = useCallback(() => {
    dispatch(
      change(
        messengerAppearanceFormName,
        messengerSettingsFormFields.logo,
        null,
      ),
    );
  }, [dispatch]);

  const handleSwitchToggled: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target: { checked } }) => {
      if (!checked && isEnabled) {
        clearSelectedFile();
      }
      setIsEnabled(checked);
    },
    [clearSelectedFile, isEnabled],
  );

  const uploadFile = useCallback(
    async (file: File) => {
      try {
        setHasError(false);
        setIsUploading(true);
        const { id: fileId } = await messengerApi.uploadLogo(file);
        dispatch(
          change(
            messengerAppearanceFormName,
            messengerSettingsFormFields.logo,
            fileId,
          ),
        );
      } catch (error) {
        setHasError(true);
        notify({
          title: 'Upload failed. Please try again.',
          type: 'error',
        });
      } finally {
        setIsUploading(false);
      }
    },
    [dispatch, messengerApi, notify],
  );

  const handleFileUpload: ChangeEventHandler<HTMLInputElement> = useCallback(
    async ({ target }) => {
      if (target instanceof HTMLInputElement) {
        const file = target.files?.item(0) ?? null;
        if (!file) {
          clearSelectedFile();
        } else {
          uploadFile(file);
        }
      }
    },
    [clearSelectedFile, uploadFile],
  );

  const handleTryAgain = useCallback(() => {
    const file = fileInput.current?.files?.item(0) ?? null;
    if (file) {
      uploadFile(file);
    }
  }, [uploadFile]);

  const previewLogo = useMemo(() => resolveLogoUrl(logo), [logo]);

  return (
    <FlexBox flexDirection="column" gap="400">
      <FormSectionTitle
        title="Logo"
        description="Upload a logo in PNG format. For best results ensure a minimum height of 40 pixels. Choose a width on a scale from 1 to 5, proportionate to the height, and consider the overall image size for optimal quality and performance."
        rightSlot={
          <Switch
            name="logo"
            value=""
            checked={isEnabled}
            onChange={handleSwitchToggled}
          />
        }
      />

      {isEnabled && (
        <FlexBox gap="300" alignItems="center">
          <Box className={styles.uploadIcon} onClick={openFilePicker}>
            {previewLogo ? (
              <img
                src={previewLogo}
                alt="Logo Preview"
                className={styles.previewImage}
              />
            ) : (
              <Icon name="image" size={16} />
            )}
          </Box>
          <Button
            variant="secondary"
            onClick={openFilePicker}
            disabled={isUploading}
          >
            <>
              <FlexBox justifyContent="center">
                <Spinner
                  size={16}
                  className={classNames(styles.loadingSpinner, {
                    [styles.loadingSpinnerShown]: isUploading,
                  })}
                />
                <Box
                  className={classNames({
                    [styles.uploadTextHidden]: isUploading,
                  })}
                >
                  Upload new file
                </Box>
              </FlexBox>
              <input
                ref={fileInput}
                type="file"
                accept="image/png"
                data-testid="upload-input"
                onChange={handleFileUpload}
                className={styles.fileUpload}
              />
            </>
          </Button>
          {hasError && (
            <Button
              variant="secondary"
              onClick={handleTryAgain}
              disabled={isUploading}
            >
              Try again
            </Button>
          )}
        </FlexBox>
      )}
    </FlexBox>
  );
};
